import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { toast } from "sonner";
import { gradeStyle, recordGrade, EVALUATION_LABELS, UNIT_SHORT } from "@/lib/physical";

const GRADE_FILL = {
  Excelente: "#10b981", Notable: "#3b82f6", Bien: "#f59e0b", Mejorable: "#f43f5e", "—": "#cbd5e1",
};

export default function EvolutionDialog({ player, categoryTests, baremos, onClose }) {
  const [records, setRecords] = useState([]);
  const [testId, setTestId] = useState(categoryTests[0]?.id || "");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/physical-tests?player_id=${player.id}`);
        setRecords(r.data || []);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, [player.id]);

  const allTests = useMemo(() => {
    const map = new Map((categoryTests || []).map((t) => [t.id, t]));
    (records || []).forEach((rec) => {
      (rec.protocol || []).forEach((p) => {
        if (p.id && !map.has(p.id) && rec.results?.[p.id] != null) {
          map.set(p.id, {
            id: p.id, label: p.name || p.id, unit: p.unit || "",
            direction: p.direction || "lower", aggregation: p.aggregation || "best",
            attempts: p.attempts || 1, inactive: true,
          });
        }
      });
    });
    return [...map.values()];
  }, [categoryTests, records]);

  const tt = allTests.find((t) => t.id === testId) || allTests[0];
  const baremo = baremos[tt?.id];

  // Build an ordered series across seasons + evaluations.
  const series = useMemo(() => {
    if (!tt) return [];
    const sorted = [...records].sort((a, b) => {
      if (a.season !== b.season) return a.season < b.season ? -1 : 1;
      return a.evaluation - b.evaluation;
    });
    return sorted
      .map((rec) => {
        const v = rec.results?.[tt.id];
        if (v == null) return null;
        const grade = recordGrade(rec, tt.id, baremo, tt.direction);
        return {
          key: `${rec.season} · ${EVALUATION_LABELS[rec.evaluation]}`,
          season: rec.season,
          evaluation: rec.evaluation,
          value: Number(v),
          grade: grade.label,
          attempts: rec.attempts?.[tt.id] || [],
        };
      })
      .filter(Boolean);
  }, [records, tt, baremo]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="evolution-dialog">
        <DialogHeader>
          <DialogTitle>Evolución · {player.name}</DialogTitle>
          <DialogDescription className="text-xs">
            Comparativa entre evaluaciones (Septiembre · Enero · Mayo) y temporadas.
          </DialogDescription>
        </DialogHeader>

        <div className="mb-3 max-w-xs">
          <Label className="text-xs">Prueba</Label>
          <Select value={testId} onValueChange={setTestId}>
            <SelectTrigger data-testid="evolution-test-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {allTests.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.label}{t.inactive ? " · inactiva" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {series.length === 0 ? (
          <div className="py-8 text-sm text-slate-400 italic text-center">
            Sin resultados registrados para esta prueba.
          </div>
        ) : (
          <>
            <div className="h-64 w-full" data-testid="evolution-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="key" tick={{ fontSize: 11 }} angle={-12} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(v) => [`${v} ${UNIT_SHORT[tt.unit] || ""}`, "Resultado"]}
                    labelStyle={{ fontSize: 12 }}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {series.map((s, i) => <Cell key={i} fill={GRADE_FILL[s.grade] || "#8b5cf6"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <table className="w-full text-sm mt-4" data-testid="evolution-table">
              <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-2 py-2 text-left">Evaluación</th>
                  <th className="px-2 py-2 text-right">Resultado</th>
                  <th className="px-2 py-2 text-left">Intentos</th>
                  <th className="px-2 py-2 text-center">Clasificación</th>
                  <th className="px-2 py-2 text-right">Evolución</th>
                </tr>
              </thead>
              <tbody>
                {series.map((s, i) => {
                  const prev = series[i - 1];
                  let delta = null;
                  if (prev) {
                    const diff = s.value - prev.value;
                    const improved = tt.direction === "lower" ? diff < 0 : diff > 0;
                    delta = { diff, improved };
                  }
                  const g = gradeStyle(s.grade);
                  return (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="px-2 py-2 text-slate-700">{s.key}</td>
                      <td className="px-2 py-2 text-right font-bold">{s.value} {UNIT_SHORT[tt.unit] || ""}</td>
                      <td className="px-2 py-2 text-[11px] text-slate-500">{s.attempts.join(", ") || "—"}</td>
                      <td className="px-2 py-2 text-center">
                        <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${g.bg} ${g.color}`}>{s.grade}</span>
                      </td>
                      <td className={`px-2 py-2 text-right text-[12px] font-bold ${delta ? (delta.improved ? "text-emerald-600" : "text-rose-600") : "text-slate-300"}`}>
                        {delta ? `${delta.improved ? "▲" : "▼"} ${Math.abs(delta.diff).toFixed(2)}` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
